import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Button, ExternalLinkIcon, SimpleSpinner } from '@buffer-mono/popcorn'

import { LoadMoreContextButton } from './LoadMoreContextButton'
import { useContextComments } from './useContextComments'
import {
  CommentContextProvider,
  useCommentData,
} from '../context/CommentContext'
import type { CommentData } from '../context/CommentContext'
import { CommentThreadContent } from '../CommentCard'
import type { CommentThreadContentChannel } from '../CommentCard/CommentThreadContent'

import styles from './CommentsContextSection.module.css'

import type { JSX } from 'react'

const EASE_OUT_QUAD = [0.25, 0.46, 0.45, 0.94] as const
const EASE_IN_QUAD = [0.55, 0.085, 0.68, 0.53] as const

const CONTEXT_COMMENTS_ANIMATION = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.2, ease: EASE_OUT_QUAD },
      opacity: { duration: 0.2, ease: EASE_OUT_QUAD },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.2, ease: EASE_IN_QUAD },
      opacity: { duration: 0.2, ease: EASE_IN_QUAD },
    },
  },
} as const

const COMMENT_ITEM_ANIMATION = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: EASE_OUT_QUAD },
} as const

const BUTTON_ANIMATION = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.2, ease: EASE_OUT_QUAD },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15, ease: EASE_IN_QUAD },
  },
} as const

const ExpandContextButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void
    loading: boolean
    showSpinner: boolean
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ onClick, loading, showSpinner, ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      variant="tertiary"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={styles.expandContextButton}
      size="small"
      disabled={loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      Show more replies
      {showSpinner && (
        <span
          data-testid="loading-spinner"
          className={styles.spinnerWrapper}
          aria-hidden="true"
        >
          <SimpleSpinner size="small" />
        </span>
      )}
    </Button>
  )
})

ExpandContextButton.displayName = 'ExpandContextButton'

const ViewFullThreadLink = React.forwardRef<
  HTMLAnchorElement,
  {
    href: string
  }
>(({ href }, ref) => {
  return (
    <Button
      ref={ref}
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="tertiary"
      size="small"
      aria-label="View full thread in new tab"
      className={styles.expandContextButton}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      Show more replies
      <ExternalLinkIcon />
    </Button>
  )
})

ViewFullThreadLink.displayName = 'ViewFullThreadLink'

const CollapseContextButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ onClick, ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      variant="tertiary"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={styles.expandContextButton}
      size="small"
      {...rest}
    >
      Hide replies
    </Button>
  )
})

CollapseContextButton.displayName = 'CollapseContextButton'

type ContextButtonsProps = {
  showContext: boolean
  isLoading: boolean
  showSpinner: boolean
  externalLink?: string | null
  panelId: string
  onExpand: () => void
  onCollapse: () => void
}

const ContextButtons = ({
  showContext,
  isLoading,
  showSpinner,
  externalLink,
  panelId,
  onExpand,
  onCollapse,
}: ContextButtonsProps): JSX.Element => {
  return (
    <AnimatePresence mode="popLayout">
      {!showContext ? (
        <m.div key="expand-button" {...BUTTON_ANIMATION}>
          <ExpandContextButton
            aria-expanded={false}
            aria-controls={panelId}
            onClick={onExpand}
            loading={isLoading}
            showSpinner={showSpinner}
          />
        </m.div>
      ) : (
        <m.div
          key="expanded-buttons"
          {...BUTTON_ANIMATION}
          className={styles.expandedButtonGroup}
        >
          {externalLink && (
            <>
              <ViewFullThreadLink href={externalLink} />
              <div className={styles.verticalLine} />
            </>
          )}
          <CollapseContextButton
            aria-expanded={true}
            aria-controls={panelId}
            onClick={onCollapse}
          />
        </m.div>
      )}
    </AnimatePresence>
  )
}

type ContextCommentsListProps = {
  channel: CommentThreadContentChannel
  comments: CommentData[]
  hasMore: boolean
  totalCount: number
  visibleCount: number
  panelId: string
  onLoadMore: () => void
  onExpandNextComment?: (
    fromCommentId?: string,
    onlyRepliable?: boolean,
  ) => void
}

const ContextCommentsList = ({
  channel,
  comments,
  hasMore,
  totalCount,
  visibleCount,
  panelId,
  onLoadMore,
  onExpandNextComment,
}: ContextCommentsListProps): JSX.Element => {
  return (
    <m.div
      key="context-comments"
      id={panelId}
      data-testid="context-comments-container"
      {...CONTEXT_COMMENTS_ANIMATION}
      layout="position"
      className={styles.contextCommentsContainer}
    >
      {hasMore && (
        <LoadMoreContextButton
          remainingCount={totalCount - visibleCount}
          increment={5}
          onClick={onLoadMore}
        />
      )}
      {comments.map((contextComment) => (
        <m.div
          key={`context-${contextComment.id}`}
          {...COMMENT_ITEM_ANIMATION}
          layout="position"
        >
          <CommentContextProvider comment={contextComment}>
            <CommentThreadContent
              channel={channel}
              isContextComment={true}
              onExpandNextComment={onExpandNextComment}
              expanded={true}
            />
          </CommentContextProvider>
        </m.div>
      ))}
    </m.div>
  )
}

export const CommentsContextSection = React.forwardRef<
  HTMLDivElement,
  {
    channel: CommentThreadContentChannel
    onExpandNextComment?: (
      fromCommentId?: string,
      onlyRepliable?: boolean,
    ) => void
  }
>(({ channel, onExpandNextComment }, ref) => {
  const comment = useCommentData()
  const context = useContextComments(comment.id)

  const hasParentComment = comment.parentId !== null
  const externalLink = comment.post?.externalLink

  if (!hasParentComment) {
    return null
  }

  const panelId = `context-panel-${comment.id}`

  return (
    <div ref={ref}>
      <ContextButtons
        showContext={context.showContext}
        isLoading={context.isLoading}
        showSpinner={context.showSpinner}
        externalLink={externalLink}
        panelId={panelId}
        onExpand={() => {
          void context.handleExpandContext()
        }}
        onCollapse={context.handleCollapseContext}
      />

      <AnimatePresence>
        {context.showContextComments && (
          <ContextCommentsList
            channel={channel}
            comments={context.visibleContextComments}
            hasMore={context.hasMore}
            totalCount={context.contextComments.length}
            visibleCount={context.visibleCount}
            panelId={panelId}
            onLoadMore={context.handleShowMoreContext}
            onExpandNextComment={onExpandNextComment}
          />
        )}
      </AnimatePresence>
    </div>
  )
})

CommentsContextSection.displayName = 'CommentsContextSection'
